import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  robots?: string;
  structuredData?: Record<string, unknown>;
}

const SITE_NAME = "ZWeb Revenda";
const SITE_URL = "https://revenda.zweb.com.br";
const DEFAULT_DESCRIPTION = "Plataforma Comercial Inteligente para Revenda ZWeb. Gerencie leads, propostas, implantações e clientes em uma única plataforma.";
const DEFAULT_IMAGE = "/og-image.png";

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = [],
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_IMAGE,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonicalUrl,
  robots = "index, follow",
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonical = canonicalUrl ? `${SITE_URL}${canonicalUrl}` : undefined;

  useEffect(() => {
    // Title
    document.title = fullTitle;

    // Helper para definir meta tags
    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Meta tags básicas
    setMeta("description", description);
    if (keywords.length > 0) {
      setMeta("keywords", keywords.join(", "));
    }
    setMeta("robots", robots);
    setMeta("author", SITE_NAME);

    // Open Graph
    setMeta("og:title", ogTitle || fullTitle, true);
    setMeta("og:description", ogDescription || description, true);
    setMeta("og:image", `${SITE_URL}${ogImage}`, true);
    setMeta("og:type", ogType, true);
    setMeta("og:site_name", SITE_NAME, true);
    if (canonical) {
      setMeta("og:url", canonical, true);
    }

    // Twitter Card
    setMeta("twitter:card", twitterCard);
    setMeta("twitter:title", twitterTitle || ogTitle || fullTitle);
    setMeta("twitter:description", twitterDescription || ogDescription || description);
    setMeta("twitter:image", `${SITE_URL}${twitterImage || ogImage}`);

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!linkCanonical) {
        linkCanonical = document.createElement("link");
        linkCanonical.setAttribute("rel", "canonical");
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute("href", canonical);
    }

    // Structured Data (JSON-LD)
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [
    fullTitle,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    canonical,
    robots,
    structuredData,
  ]);

  return null;
}

// Helpers para structured data
export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+55-11-99999-9999",
      contactType: "sales",
      availableLanguage: "Portuguese",
    },
    sameAs: [
      "https://linkedin.com/company/zweb",
      "https://instagram.com/zweb",
      "https://youtube.com/zweb",
    ],
  };
}

export function getArticleSchema(article: {
  title: string;
  description: string;
  publishedAt: string;
  updatedAt?: string;
  image?: string;
  authorName?: string;
  slug: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.image ? `${SITE_URL}${article.image}` : `${SITE_URL}${DEFAULT_IMAGE}`,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: {
      "@type": "Person",
      name: article.authorName || "Equipe ZWeb",
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${article.slug}`,
    },
  };
}

export function getFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function getProductSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ZWeb ERP",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: [
      {
        "@type": "Offer",
        name: "Starter",
        price: "299",
        priceCurrency: "BRL",
      },
      {
        "@type": "Offer",
        name: "Professional",
        price: "799",
        priceCurrency: "BRL",
      },
      {
        "@type": "Offer",
        name: "Enterprise",
        price: "1999",
        priceCurrency: "BRL",
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "127",
    },
  };
}

export function getHowToSchema(tutorial: {
  title: string;
  description: string;
  estimatedTime?: number;
  slug: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: tutorial.title,
    description: tutorial.description,
    totalTime: tutorial.estimatedTime ? `PT${tutorial.estimatedTime}M` : undefined,
    url: `${SITE_URL}/tutoriais/${tutorial.slug}`,
  };
}
